import { Stepper } from '@innovationnorway/component-library';
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import useSiteimprove from 'src/hooks/use-siteimprove';
import { SITEIMPROVE_DECLARATIONS } from 'src/features/tracking/constants';
import { PageParams } from '../../types/PageParams';
import TertiaryButton from 'src/components/TertiaryButton/TertiaryButton';
import { Alert, Button } from '@innovationnorway/cl-react';

type Props = {
  step: number;
  numberOfSelectedRows: number;
  handleSendReferral: () => void;
  actorMessageEmpty: boolean;
  customerMessageEmpty: boolean;
  customerEmailSelection: boolean;
  handleStepChange: (prev: number, next: number) => void;
  shareOpportunityError: boolean;
  askIfUserWantsToArchive?: boolean;
};

const NavigationBar: React.FC<Props> = ({
  step,
  numberOfSelectedRows,
  handleSendReferral,
  actorMessageEmpty,
  customerMessageEmpty,
  customerEmailSelection,
  handleStepChange,
  shareOpportunityError,
}) => {
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const { t: tCommon } = useTranslation('common');
  const { formId } = useParams<PageParams>();
  const siteimprove = useSiteimprove();
  const [notificationVisible, setNotificationVisible] = useState(false);
  const stepCount = 4;

  const NavigationPrimaryButton = () => {
    const handlePrimaryButtonClick = () => {
      if (
        (step === 0 && numberOfSelectedRows === 0) ||
        (step === 2 && (actorMessageEmpty || (customerEmailSelection && customerMessageEmpty)))
      ) {
        setNotificationVisible(true);
        return;
      }
      if (step === 3) {
        handleSendReferral();
        return;
      }

      siteimprove.log(
        SITEIMPROVE_DECLARATIONS.FORWARDOPPORTUNITY.id,
        step === 1
          ? SITEIMPROVE_DECLARATIONS.FORWARDOPPORTUNITY.actions.GO_TO_PREVIEW
          : SITEIMPROVE_DECLARATIONS.FORWARDOPPORTUNITY.actions.START_REFERRAL,
        formId,
      );

      handleStepChange(step, step + 1);
    };

    return (
      <Button onClick={handlePrimaryButtonClick}>
        {step === 0 && tCommon('next')}
        {step === 1 && tCommon('next')}
        {step === 2 && tCommon('next')}
        {step === 3 && tOneWayIn('referral.navigationBar.step3.primaryButtonText')}
      </Button>
    );
  };

  const stepText = useMemo(() => {
    switch (step) {
      case 0:
        return tOneWayIn('referring.navigationBar.stepText.step0');
      case 1:
        return tOneWayIn('referring.navigationBar.stepText.step1');
      case 2:
        return tOneWayIn('referring.navigationBar.stepText.step2');
      case 3:
        return tOneWayIn('referring.navigationBar.stepText.step3');
      default:
        return '';
    }
  }, [step, tOneWayIn]);

  const stepSubText = useMemo(() => {
    switch (step) {
      case 2:
        return tOneWayIn('referring.navigationBar.stepSubText.step2');
      default:
        return '';
    }
  }, [step, tOneWayIn]);

  const NavigationSecondayButton = () => {
    return (
      <TertiaryButton
        onClick={() => {
          siteimprove.log(
            SITEIMPROVE_DECLARATIONS.FORWARDOPPORTUNITY.id,
            step === 1
              ? SITEIMPROVE_DECLARATIONS.FORWARDOPPORTUNITY.actions.BACK_TO_CHOOSE_ACTOR
              : SITEIMPROVE_DECLARATIONS.FORWARDOPPORTUNITY.actions.BACK_TO_WRITE_REFERRAL,
            formId,
          );

          handleStepChange(step, step - 1);
        }}
      >
        {tCommon('previous')}
      </TertiaryButton>
    );
  };

  const notificationErrorContent = useMemo(() => {
    if (step === 0) {
      if (numberOfSelectedRows === 0) {
        return tOneWayIn('referral.notification.step1.error.noneSelected');
      }
    } else if (step === 2) {
      if (actorMessageEmpty) {
        return tOneWayIn('referral.notification.step2.error.noActorMessage');
      }
      if (customerEmailSelection && customerMessageEmpty) {
        return tOneWayIn('referral.notification.step2.error.noCustomerMessage');
      }
    } else if (step === 3 && shareOpportunityError) {
      return (
        <>
          {tOneWayIn('referral.notification.step3.error')}
          <a href="maito:support@enveiinn.no">support@enveiinn.no</a>
        </>
      );
    }
  }, [
    actorMessageEmpty,
    customerEmailSelection,
    customerMessageEmpty,
    numberOfSelectedRows,
    shareOpportunityError,
    step,
    tOneWayIn,
  ]);

  const notificationErrorVisible = useMemo(() => {
    if (step === 0 && notificationVisible && numberOfSelectedRows === 0) {
      return true;
    }
    if (
      step === 2 &&
      notificationVisible &&
      (actorMessageEmpty || (customerEmailSelection && customerMessageEmpty))
    ) {
      return true;
    }
    if (step === 3 && shareOpportunityError) {
      return true;
    }
    return false;
  }, [
    actorMessageEmpty,
    customerEmailSelection,
    customerMessageEmpty,
    notificationVisible,
    numberOfSelectedRows,
    shareOpportunityError,
    step,
  ]);

  return (
    <div className="navigation-bar__container">
      <div className="navigation-bar__content">
        <div className="display--flex flex-direction--column">
          <Stepper
            className="margin-left--0 padding-bottom--0 stepper"
            currentStep={step}
            stepCount={stepCount}
          />
          <div className="display--flex flex-direction--column gap--1">
            <span className="font-weight--bold">{stepText}</span>
            <div className="display--flex flex-direction--row padding--0 gap--1">
              <span className="font-size--x-large">{stepSubText}</span>
            </div>
          </div>
        </div>
        {notificationErrorVisible && (
          <Alert data-color="danger" className="width--100">
            {notificationErrorContent}
          </Alert>
        )}
        <div className="display--flex flex-direction--row gap--1">
          {step !== 0 && <NavigationSecondayButton />}
          <NavigationPrimaryButton />
        </div>
      </div>
    </div>
  );
};

export default NavigationBar;
